import React, { useState } from "react";
import { Check, ChevronsUpDown, Plus } from "lucide-react";

import { Button } from "src/components/ui/Button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "src/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "src/components/ui/popover";
import { cn } from "src/lib/utils";

export type PersonCommandOption = {
  value: string;
  label: string;
  description: string;
  keywords?: string;
  recent?: boolean;
};

interface PersonCommandComboboxProps {
  id: string;
  value: string;
  onChange: (value: string) => void;
  options: PersonCommandOption[];
  placeholder: string;
  searchPlaceholder: string;
  emptyLabel: string;
  createLabel: string;
  icon: React.ReactNode;
  disabled?: boolean;
}

const PersonCommandCombobox = ({
  id,
  value,
  onChange,
  options,
  placeholder,
  searchPlaceholder,
  emptyLabel,
  createLabel,
  icon,
  disabled = false,
}: PersonCommandComboboxProps) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const selectedOption = options.find((option) => option.value === value);
  const hasTypedValue = search.trim().length > 0;
  const recentOptions = options.filter((option) => option.recent);
  const allOptions = options.filter((option) => !option.recent);
  const displayValue = selectedOption?.label || value;

  const handleSelect = (selectedValue: string) => {
    onChange(selectedValue);
    setSearch("");
    setOpen(false);
  };

  const handleCreate = () => {
    if (!hasTypedValue) return;
    handleSelect(search.trim());
  };

  const renderOption = (option: PersonCommandOption) => (
    <CommandItem
      key={option.value}
      value={`${option.label} ${option.description} ${option.keywords || ""}`}
      onSelect={() => handleSelect(option.value)}
      className="gap-3 px-3 py-3"
    >
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-blue-50 text-blue-600">
        {icon}
      </div>
      <div className="min-w-0 flex-1">
        <div className="truncate font-medium text-gray-900">{option.label}</div>
        <div className="truncate text-xs text-gray-500">{option.description}</div>
      </div>
      <Check
        className={cn(
          "h-4 w-4 text-blue-600",
          value === option.value ? "opacity-100" : "opacity-0"
        )}
      />
    </CommandItem>
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className="h-11 w-full justify-between px-3 font-normal"
        >
          <span className={cn("truncate", !displayValue && "text-muted-foreground")}>
            {displayValue || placeholder}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command shouldFilter>
          <CommandInput
            value={search}
            onValueChange={setSearch}
            placeholder={searchPlaceholder}
          />
          <CommandList>
            <CommandEmpty>{emptyLabel}</CommandEmpty>
            {recentOptions.length > 0 && (
              <CommandGroup heading="Recent">
                {recentOptions.map(renderOption)}
              </CommandGroup>
            )}
            {allOptions.length > 0 && (
              <>
                <CommandSeparator />
                <CommandGroup heading="All">
                  {allOptions.map(renderOption)}
                </CommandGroup>
              </>
            )}
            {hasTypedValue && (
              <>
                <CommandSeparator />
                <CommandGroup>
                  <CommandItem
                    value={`${createLabel} ${search}`}
                    onSelect={handleCreate}
                    className="gap-3 px-3 py-3"
                  >
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gray-100 text-gray-600">
                      <Plus className="h-4 w-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-gray-900">{createLabel}</div>
                      <div className="truncate text-xs text-gray-500">{search.trim()}</div>
                    </div>
                  </CommandItem>
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export { PersonCommandCombobox };
